/*
 *  -----------------------------------------------------------------------
 *    <copyright file="manage-signatories-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { Signatory } from '@interfaces/report-templates.interface';
import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import DeleteSignatory from '@components/reports/delete-signatory';
import { MANAGE_SIGNATORIES_COLUMNS } from '@components/shared/manage-signatories-grid/manage-signatories-grid.data';
import { prepareSignatoriesData } from '@components/shared/manage-signatories-grid/manage-signatories-grid.helper';
import { ManageSignatoriesGridProps } from '@components/shared/manage-signatories-grid/manage-signatories-grid.interface';
import ManageSignatoryDialog from 'views/components/shared/manage-signatories-grid/manage-signatory-dialog';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants, { ModuleNames } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import NotificationService from '@utils/notification';

import './styles.scss';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import Date from '@components/core/date';
import moment from 'moment';

const ManageSignatoriesGrid = ({
    items,
    canEdit,
    module,
    onCreateUpdate,
    onRefetch,
    onDelete,
    isLoading,
}: ManageSignatoriesGridProps) => {
    const { state: localState } = useLocalState();
    const studyId = localState.selectedStudy?.id;
    const studyName = localState?.selectedStudy?.shortName;

    const { t } = useTranslation();
    const [reportSignatoriesList, setSignatoriesList] = useState<Signatory[]>([]);
    const [currentSignatory, setCurrentSignatory] = useState<Signatory | null>(null);
    const openAddSignatory = useBoolean(false);
    const showDeleteSignatory = useBoolean(false);

    const moduleTitle =
        module === Constants.Modules.CM ? ModuleNames.CM : module === Constants.Modules.RAM ? ModuleNames.RAM : '';

    const exportFileName = `${studyName}_${moduleTitle} Signatories_${stringDateWithoutTimeFromUtc(
        moment().toDate(),
        'DD-MMM-YYYY',
    )}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    const restructuredSignatories = (templates: Signatory[]) => {
        if (templates) {
            templates = restructuredUpdatedCreatedBy(templates);
            setSignatoriesList(orderBy(templates, [{ field: 'lastName', dir: 'desc' }]) ?? []);
        }
    };

    useEffect(() => {
        restructuredSignatories(items);
    }, [items]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('cm:addSignatory'),
            onClick: () => openAddSignatory.setTrue(),
            customClassName: 'grid-header__item--always-visible',
            icon: ['fal', 'plus'],
            disabled: !canEdit,
            testId: 'add-signatory',
        },
    ];

    const actions = (signatory: Signatory): IActionItemV2[] => [
        {
            label: 'cm:editSignatory',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentSignatory(signatory);
                openAddSignatory.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'cm:deleteSignatory',
            icon: ['fal', 'trash'],
            onClick: () => {
                setCurrentSignatory(signatory);
                showDeleteSignatory.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const onCreateUpdateClose = () => {
        openAddSignatory.setFalse();
        if (currentSignatory) setCurrentSignatory(null);
    };

    const onDeleteClose = () => {
        showDeleteSignatory.setFalse();
        if (currentSignatory) setCurrentSignatory(null);
    };

    const createSignatory = (data: Signatory) => {
        const createData = prepareSignatoriesData(data, module, studyId);

        onCreateUpdate(createData)
            .then(() => {
                onRefetch().catch((err) => window.console.error(err));
                NotificationService.showNotification(t('cm:signatorySuccessfulCreate'));
                onCreateUpdateClose();
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    const updateSignatory = (data: Signatory) => {
        const updateData = prepareSignatoriesData(data, module, studyId);

        onCreateUpdate(updateData)
            .then(() => {
                onRefetch().catch((err) => window.console.error(err));
                NotificationService.showNotification(t('cm:signatorySuccessfulUpdated', { id: data?.recordId }));
                onCreateUpdateClose();
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    const deleteSignatory = async () => {
        const res = await onDelete(currentSignatory?.id ?? '', module);

        if (res?.[0]) {
            onRefetch().catch((err) => window.console.error(err));
            NotificationService.showNotification(
                t('cm:signatorySuccessfulDeleted', { id: currentSignatory?.recordId }),
            );
            onDeleteClose();
        } else {
            NotificationService.showNotification(t('errorMessage'));
        }
    };

    return (
        <>
            {openAddSignatory.value && (
                <ManageSignatoryDialog
                    currentSignatory={currentSignatory}
                    onCreate={createSignatory}
                    onUpdate={updateSignatory}
                    onClose={() => onCreateUpdateClose()}
                    isLoading={isLoading}
                />
            )}
            {showDeleteSignatory.value && !!currentSignatory && (
                <DeleteSignatory
                    open
                    currentSignatory={currentSignatory}
                    onClose={() => onDeleteClose()}
                    canEdit={canEdit}
                    onDelete={() => deleteSignatory()}
                    isLoading={isLoading}
                />
            )}
            <ExpandableGridV2
                title={t('cm:Signatories')}
                className="manage-signatories-grid"
                hideCheckbox
                hideExpand
                customKey="signatoriesList"
                key="signatories-list"
                id="signatories-list"
                items={reportSignatoriesList}
                rowActions={actions}
                scrollable="scrollable"
                groupable
                reorderable
                resizable
                pageable={false}
                columns={MANAGE_SIGNATORIES_COLUMNS(t)}
                extraMenuItems={extraMenuItems}
                exportConfig={exportConfig}
            />
        </>
    );
};

export default ManageSignatoriesGrid;
